import { useCallback, useMemo, useRef, useState } from 'react'
import { FileDown, FileUp, PlusIcon, ShieldCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  useCreateSafetyFunction,
  useDeleteSafetyFunction,
  useExportSistemaSsm,
  useGetMachineryDirectiveCompliance,
  useGetMachineryDirectiveEPlan,
  useImportSistemaFile,
  useListSafetyFunctions,
  usePreviewSistemaImport,
  useStartEplanSafetyFunctionExtraction,
  useStartEplanSafetyFunctionImport,
  useUpdateSafetyFunction,
  type EplanSafetyFunctionExtractionStreamEvent,
  type EplanSafetyFunctionProposal,
  type SafetyFunctionWithArchitecture,
  type SistemaImportPreview,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { ComplianceSubPageHeader } from '../../components/ComplianceSubPageHeader'
import { ComplianceSubPageTitle } from '../../components/ComplianceSubPageTitle'
import { buildMachineryDirectiveStudioBaseHref } from '../../machineryDirectiveStudioPaths'
import { CreateSafetyFunctionDialog } from './CreateSafetyFunctionDialog'
import { DeleteSafetyFunctionDialog } from './DeleteSafetyFunctionDialog'
import { ExportSistemaDialog } from './ExportSistemaDialog'
import {
  buildImportedEplanExtractedFunctionIds,
  ExtractFromEplanModal,
} from './ExtractFromEplanModal'
import { ImportSistemaDialog } from './ImportSistemaDialog'
import { SafetyFunctionOverviewCard } from './SafetyFunctionOverviewCard'
import {
  ExtractFromEplanButton,
  type EplanExtractionReadiness,
} from './ExtractFromEplanButton'

export function SafetyFunctionsPage() {
  const params = useParams<{
    machineryDirectiveComplianceId: string
    productId: string
  }>()
  const complianceId = params.machineryDirectiveComplianceId!
  const productId = params.productId
  const { t } = useTranslation('machineryDirectiveCompliance')

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isExtractFromEplanDialogOpen, setIsExtractFromEplanDialogOpen] =
    useState(false)
  const [eplanProposals, setEplanProposals] = useState<
    EplanSafetyFunctionProposal[]
  >([])
  const [eplanExtractionError, setEplanExtractionError] = useState<
    string | null
  >(null)
  const [eplanProgressMessage, setEplanProgressMessage] = useState<
    string | null
  >(null)
  const [eplanProgressCurrent, setEplanProgressCurrent] = useState<
    number | null
  >(null)
  const [eplanProgressTotal, setEplanProgressTotal] = useState<number | null>(
    null
  )
  const eplanProgressCurrentRef = useRef<number | null>(null)
  const eplanProgressTotalRef = useRef<number | null>(null)
  const [renamingSafetyFunction, setRenamingSafetyFunction] =
    useState<SafetyFunctionWithArchitecture | null>(null)
  const [deletingSafetyFunctionId, setDeletingSafetyFunctionId] = useState<
    string | null
  >(null)
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false)
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false)
  const [importFileName, setImportFileName] = useState<string | null>(null)
  const [importPreview, setImportPreview] =
    useState<SistemaImportPreview | null>(null)
  const [importFileContent, setImportFileContent] = useState<string | null>(
    null
  )
  const [importPreviewError, setImportPreviewError] = useState<string | null>(
    null
  )

  const {
    data: safetyFunctions = [],
    isLoading,
    error,
  } = useListSafetyFunctions(complianceId)
  const { data: compliance } = useGetMachineryDirectiveCompliance(complianceId)
  const { data: eplanData } = useGetMachineryDirectiveEPlan(complianceId)
  const eplanSource = eplanData?.productSource ?? null

  const createMutation = useCreateSafetyFunction(complianceId)
  const updateMutation = useUpdateSafetyFunction(complianceId)
  const deleteMutation = useDeleteSafetyFunction(complianceId)
  const previewImportMutation = usePreviewSistemaImport(complianceId)
  const importMutation = useImportSistemaFile(complianceId)
  const exportSistemaSsm = useExportSistemaSsm(complianceId)
  const startEplanExtraction =
    useStartEplanSafetyFunctionExtraction(complianceId)
  const startEplanImport = useStartEplanSafetyFunctionImport(complianceId)

  const resolvedProductId = productId ?? compliance?.productId

  const importedEplanExtractedFunctionIds = useMemo(
    () => buildImportedEplanExtractedFunctionIds(safetyFunctions),
    [safetyFunctions]
  )

  const eplanReadiness = useMemo((): EplanExtractionReadiness => {
    if (eplanSource == null) {
      return 'none'
    }
    if (eplanSource.fileUploadStatus === 'PENDING') {
      return 'uploading'
    }
    if (
      eplanSource.fileUploadStatus === 'FAILED' ||
      eplanSource.ingestionStatus === 'FAILED'
    ) {
      return 'failed'
    }
    if (
      eplanSource.ingestionStatus === 'PROCESSING' ||
      eplanSource.ingestionStatus === 'PENDING'
    ) {
      return 'processing'
    }
    if (eplanSource.ingestionStatus === 'COMPLETED') {
      return 'ready'
    }
    return 'processing'
  }, [eplanSource])

  const eplanSettingsHref =
    resolvedProductId != null
      ? `${buildMachineryDirectiveStudioBaseHref(resolvedProductId, complianceId)}/settings/e-plan`
      : null

  const isExtractFromEplanEnabled = eplanReadiness === 'ready'

  const isMutationPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    previewImportMutation.isPending ||
    importMutation.isPending ||
    exportSistemaSsm.isPending ||
    startEplanExtraction.isPending ||
    startEplanImport.isPending

  const resetEplanExtractionState = useCallback(() => {
    setEplanProposals([])
    setEplanExtractionError(null)
    setEplanProgressMessage(null)
    setEplanProgressCurrent(null)
    setEplanProgressTotal(null)
    eplanProgressCurrentRef.current = null
    eplanProgressTotalRef.current = null
  }, [])

  const handleOpenExtractFromEplan = () => {
    if (!isExtractFromEplanEnabled) {
      return
    }
    resetEplanExtractionState()
    setIsExtractFromEplanDialogOpen(true)
  }

  const handleCloseExtractFromEplan = () => {
    if (startEplanExtraction.isPending || startEplanImport.isPending) {
      return
    }
    setIsExtractFromEplanDialogOpen(false)
    resetEplanExtractionState()
  }

  const handleEplanExtractionEvent = useCallback(
    (event: EplanSafetyFunctionExtractionStreamEvent) => {
      switch (event.type) {
        case 'progress':
          setEplanProgressMessage(event.message)
          break
        case 'safety_function.extracted':
          eplanProgressCurrentRef.current = event.functionIndex
          eplanProgressTotalRef.current = event.totalFunctions
          setEplanProgressCurrent(event.functionIndex)
          setEplanProgressTotal(event.totalFunctions)
          setEplanProgressMessage(null)
          break
        case 'step.started':
          if (event.step === 'extract_functions') {
            setEplanProgressMessage(
              t('safetyFunctionsPage.extractFromEplanModal.extractingFunctions')
            )
          }
          if (event.step === 'generate_architecture') {
            setEplanProgressMessage(
              t(
                'safetyFunctionsPage.extractFromEplanModal.generatingArchitecture'
              )
            )
          }
          break
        default:
          break
      }
    },
    [t]
  )

  const handleStartEplanExtraction = useCallback(
    (regenerate?: boolean) => {
      setEplanExtractionError(null)
      setEplanProgressMessage(null)
      setEplanProgressCurrent(null)
      setEplanProgressTotal(null)
      if (regenerate === true) {
        setEplanProposals([])
      }

      startEplanExtraction.mutate(
        {
          regenerate,
          onEvent: handleEplanExtractionEvent,
          onComplete: ({ proposals }) => {
            setEplanProposals(proposals)
            setEplanProgressMessage(null)
            setEplanProgressCurrent(null)
            setEplanProgressTotal(null)
          },
        },
        {
          onError: (err) => {
            setEplanExtractionError(err.message)
            toast.error(
              t('safetyFunctionsPage.extractFromEplanExtractionFailed')
            )
          },
        }
      )
    },
    [handleEplanExtractionEvent, startEplanExtraction, t]
  )

  const handleImportFromEplan = useCallback(
    (
      selectedFunctionIds: string[],
      editedProposals: EplanSafetyFunctionProposal[]
    ) => {
      startEplanImport.mutate(
        {
          selectedFunctionIds,
          proposals: editedProposals,
          onComplete: () => {
            toast.success(
              t('safetyFunctionsPage.extractFromEplanImportSuccess')
            )
            setIsExtractFromEplanDialogOpen(false)
            resetEplanExtractionState()
          },
        },
        {
          onError: (err) => {
            toast.error(
              t('safetyFunctionsPage.extractFromEplanImportFailed', {
                message: err.message,
              })
            )
          },
        }
      )
    },
    [resetEplanExtractionState, startEplanImport, t]
  )

  const handleCreate = async (payload: {
    name: string
    description: string
  }) => {
    try {
      await createMutation.mutateAsync({
        name: payload.name,
        description: payload.description || null,
      })
      toast.success(t('safetyFunctionsPage.createSuccess'))
      setIsCreateDialogOpen(false)
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : t('safetyFunctionsPage.createFailure')
      toast.error(message)
    }
  }

  const handleRename = async (payload: {
    safetyFunctionId: string
    name: string
    description: string
  }) => {
    try {
      await updateMutation.mutateAsync({
        safetyFunctionId: payload.safetyFunctionId,
        name: payload.name,
        description: payload.description || null,
      })
      toast.success(t('actions.saved'))
      setRenamingSafetyFunction(null)
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : t('safetyFunctionsPage.updateFailure')
      toast.error(message)
    }
  }

  const handleConfirmDelete = async () => {
    if (deletingSafetyFunctionId == null) {
      return
    }
    try {
      await deleteMutation.mutateAsync(deletingSafetyFunctionId)
      toast.success(t('safetyFunctionsPage.deleteSuccess'))
      setDeletingSafetyFunctionId(null)
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : t('safetyFunctionsPage.deleteFailure')
      toast.error(message)
    }
  }

  const resetImportState = () => {
    setImportFileName(null)
    setImportPreview(null)
    setImportFileContent(null)
    setImportPreviewError(null)
  }

  const handleOpenImportDialog = () => {
    resetImportState()
    setIsImportDialogOpen(true)
  }

  const handleCloseImportDialog = () => {
    if (importMutation.isPending || previewImportMutation.isPending) {
      return
    }
    setIsImportDialogOpen(false)
    resetImportState()
  }

  const handlePickImportFile = async (file: File) => {
    setImportFileName(file.name)
    setImportPreview(null)
    setImportFileContent(null)
    setImportPreviewError(null)

    try {
      const content = await file.text()
      setImportFileContent(content)
      const preview = await previewImportMutation.mutateAsync({ content })
      setImportPreview(preview)
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : t('safetyFunctionsPage.importDialog.previewFailure')
      setImportPreviewError(message)
    }
  }

  const handleConfirmImport = async () => {
    if (importFileContent == null) {
      return
    }
    try {
      const result = await importMutation.mutateAsync({
        content: importFileContent,
      })
      toast.success(
        t('safetyFunctionsPage.importDialog.success', {
          count: result.imported.safetyFunctionCount,
        })
      )
      setIsImportDialogOpen(false)
      resetImportState()
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : t('safetyFunctionsPage.importDialog.importFailure')
      toast.error(message)
    }
  }

  const handleConfirmExport = () => {
    exportSistemaSsm.mutate(undefined, {
      onSuccess: () => {
        toast.success(t('safetyFunctionsPage.exportSistemaSuccess'))
        setIsExportDialogOpen(false)
      },
      onError: (err) => {
        toast.error(
          err.message ?? t('safetyFunctionsPage.exportSistemaFailure')
        )
      },
    })
  }

  if (isLoading) {
    return (
      <div className='space-y-4'>
        <Skeleton className='h-8 w-64' />
        <Skeleton className='h-48 w-full' />
      </div>
    )
  }

  const hasError = error != null
  const hasSafetyFunctions = safetyFunctions.length > 0

  return (
    <div className='space-y-4'>
      <ComplianceSubPageHeader className='flex-col items-start sm:flex-col sm:items-start'>
        <div className='space-y-1'>
          <ComplianceSubPageTitle>
            {t('safetyFunctionsPage.title')}
          </ComplianceSubPageTitle>
          <p className='text-sm text-muted-foreground'>
            {t('safetyFunctionsPage.description')}
          </p>
        </div>
        <div className='flex flex-wrap gap-2'>
          <Button
            onClick={() => setIsCreateDialogOpen(true)}
            disabled={isMutationPending}
          >
            <PlusIcon className='mr-2 size-4' />
            {t('safetyFunctionsPage.addSafetyFunction')}
          </Button>
          <Button
            variant='outline'
            onClick={handleOpenImportDialog}
            disabled={isMutationPending}
          >
            <FileUp className='mr-2 size-4' />
            {t('safetyFunctionsPage.importSistemaFile')}
          </Button>
          <Button
            variant='outline'
            onClick={() => setIsExportDialogOpen(true)}
            disabled={isMutationPending || !hasSafetyFunctions}
          >
            <FileDown className='mr-2 size-4' />
            {t('safetyFunctionsPage.exportSistemaFile')}
          </Button>
          <ExtractFromEplanButton
            readiness={eplanReadiness}
            eplanSettingsHref={eplanSettingsHref}
            disabled={isMutationPending || !isExtractFromEplanEnabled}
            isPending={startEplanExtraction.isPending}
            onClick={handleOpenExtractFromEplan}
          />
        </div>
      </ComplianceSubPageHeader>

      {hasError ? (
        <Alert variant='destructive'>
          <AlertTitle>{t('safetyFunctionsPage.errorTitle')}</AlertTitle>
          <AlertDescription>{(error as Error).message}</AlertDescription>
        </Alert>
      ) : null}

      {!hasError && !hasSafetyFunctions ? (
        <Empty className='min-h-[320px] rounded-lg border border-dashed'>
          <EmptyHeader>
            <EmptyMedia variant='icon'>
              <ShieldCheck className='size-10' />
            </EmptyMedia>
            <EmptyTitle>{t('safetyFunctionsPage.emptyState.title')}</EmptyTitle>
            <EmptyDescription>
              {t('safetyFunctionsPage.emptyState.description')}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <div className='flex flex-wrap justify-center gap-2'>
              <Button onClick={() => setIsCreateDialogOpen(true)} size='sm'>
                {t('safetyFunctionsPage.emptyState.addFirst')}
              </Button>
              <Button
                size='sm'
                variant='outline'
                onClick={handleOpenImportDialog}
                disabled={isMutationPending}
              >
                <FileUp className='mr-2 size-4' />
                {t('safetyFunctionsPage.importSistemaFile')}
              </Button>
              <ExtractFromEplanButton
                readiness={eplanReadiness}
                eplanSettingsHref={eplanSettingsHref}
                disabled={isMutationPending || !isExtractFromEplanEnabled}
                isPending={startEplanExtraction.isPending}
                onClick={handleOpenExtractFromEplan}
                size='sm'
              />
            </div>
          </EmptyContent>
        </Empty>
      ) : null}

      {!hasError && hasSafetyFunctions ? (
        <div className='grid gap-4'>
          {safetyFunctions.map((safetyFunction) => (
            <SafetyFunctionOverviewCard
              key={safetyFunction.id}
              safetyFunction={safetyFunction}
              onRename={setRenamingSafetyFunction}
              onDelete={setDeletingSafetyFunctionId}
              isMutationPending={isMutationPending}
            />
          ))}
        </div>
      ) : null}

      <CreateSafetyFunctionDialog
        key={renamingSafetyFunction?.id ?? 'create-safety-function'}
        isOpen={isCreateDialogOpen || renamingSafetyFunction != null}
        onClose={() => {
          setIsCreateDialogOpen(false)
          setRenamingSafetyFunction(null)
        }}
        onCreate={handleCreate}
        safetyFunction={renamingSafetyFunction}
        onUpdate={renamingSafetyFunction ? handleRename : undefined}
        isSaving={createMutation.isPending || updateMutation.isPending}
      />

      <DeleteSafetyFunctionDialog
        isOpen={deletingSafetyFunctionId != null}
        onClose={() => setDeletingSafetyFunctionId(null)}
        onConfirm={handleConfirmDelete}
        isDeleting={deleteMutation.isPending}
      />

      <ImportSistemaDialog
        isOpen={isImportDialogOpen}
        onClose={handleCloseImportDialog}
        preview={importPreview}
        fileName={importFileName}
        isPreviewLoading={previewImportMutation.isPending}
        isImporting={importMutation.isPending}
        previewError={importPreviewError}
        onPickFile={handlePickImportFile}
        onConfirmImport={handleConfirmImport}
      />

      <ExportSistemaDialog
        isOpen={isExportDialogOpen}
        onClose={() => setIsExportDialogOpen(false)}
        onConfirm={handleConfirmExport}
        safetyFunctions={safetyFunctions}
        isExporting={exportSistemaSsm.isPending}
      />

      <ExtractFromEplanModal
        isOpen={isExtractFromEplanDialogOpen}
        onClose={handleCloseExtractFromEplan}
        productId={resolvedProductId ?? undefined}
        eplanSource={eplanSource}
        proposals={eplanProposals}
        isExtracting={startEplanExtraction.isPending}
        isImporting={startEplanImport.isPending}
        progressMessage={eplanProgressMessage}
        progressCurrent={eplanProgressCurrent}
        progressTotal={eplanProgressTotal}
        extractionError={eplanExtractionError}
        importedEplanExtractedFunctionIds={importedEplanExtractedFunctionIds}
        onStartExtraction={handleStartEplanExtraction}
        onImport={handleImportFromEplan}
        onProposalsChange={setEplanProposals}
      />
    </div>
  )
}
